/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Product API Service
 * CRUD endpoints for /api/v1/venta/products
 */

import { get, post, put, patch, del } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type {
  ProductListItem, ProductDetail, CreateProductRequest, UpdateProductRequest,
  PagedResult, ProductVariantItem, UpdateProductVariantRequest, RegenerateVariantsRequest,
  StockBatch, ProductPriceHistory, UpdateSellingPriceRequest, ProductMovement,
  ProductBarcodeMatch,
} from '../types/product';

const BASE = '/venta/products';

export interface ProductListParams {
  search?: string;
  categoryId?: string;
  status?: string;
  variantType?: string;
  locationId?: string;
  page?: number;
  size?: number;
  sort?: string;
  direction?: string;
}

export async function getProducts(params: ProductListParams = {}): Promise<PagedResult<ProductListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.search) queryParams.search = params.search;
  if (params.categoryId) queryParams.categoryId = params.categoryId;
  if (params.status) queryParams.status = params.status;
  if (params.variantType) queryParams.variantType = params.variantType;
  if (params.locationId) queryParams.locationId = params.locationId;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<ProductListItem>>>(BASE, queryParams);
  return response.data;
}

export async function getProductBatches(id: string): Promise<StockBatch[]> {
  const response = await get<ApiResponse<StockBatch[]>>(`${BASE}/${id}/batches`);
  return response.data;
}

export async function getProduct(id: string): Promise<ProductDetail> {
  const response = await get<ApiResponse<ProductDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function createProduct(data: CreateProductRequest): Promise<ProductDetail> {
  const response = await post<ApiResponse<ProductDetail>>(BASE, data);
  return response.data;
}

export async function updateProduct(id: string, data: UpdateProductRequest): Promise<ProductDetail> {
  const response = await put<ApiResponse<ProductDetail>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function deleteProduct(id: string): Promise<void> {
  await del<ApiResponse<void>>(`${BASE}/${id}`);
}

export async function getProductVariants(productId: string): Promise<ProductVariantItem[]> {
  const response = await get<ApiResponse<ProductVariantItem[]>>(`${BASE}/${productId}/variants`);
  return response.data;
}

export async function updateProductVariant(variantId: string, data: UpdateProductVariantRequest): Promise<ProductVariantItem> {
  const response = await put<ApiResponse<ProductVariantItem>>(`${BASE}/variants/${variantId}`, data);
  return response.data;
}

export async function regenerateVariants(productId: string, data: RegenerateVariantsRequest): Promise<ProductVariantItem[]> {
  const response = await post<ApiResponse<ProductVariantItem[]>>(`${BASE}/${productId}/variants/regenerate`, data);
  return response.data;
}

export async function getActiveProducts(): Promise<import('../types/quotation').ActiveProduct[]> {
  const response = await get<ApiResponse<import('../types/quotation').ActiveProduct[]>>(`${BASE}/active`);
  return response.data;
}

export async function generateInternalCode(): Promise<string> {
  const response = await post<ApiResponse<string>>(`${BASE}/generate-internal-code`);
  return response.data;
}

export async function generateBarcode(): Promise<string> {
  const response = await post<ApiResponse<string>>(`${BASE}/generate-barcode`);
  return response.data;
}

export async function resolveProductByBarcode(code: string): Promise<ProductBarcodeMatch> {
  const response = await get<ApiResponse<ProductBarcodeMatch>>(`${BASE}/barcode/${encodeURIComponent(code)}`);
  return response.data;
}

export async function updateSellingPrice(id: string, body: UpdateSellingPriceRequest): Promise<ProductDetail> {
  const response = await patch<ApiResponse<ProductDetail>>(`${BASE}/${id}/price`, body);
  return response.data;
}

export async function getPriceHistory(id: string): Promise<ProductPriceHistory[]> {
  const response = await get<ApiResponse<ProductPriceHistory[]>>(`${BASE}/${id}/price-history`);
  return response.data;
}

export async function getProductMovements(
  id: string,
  category?: string,
  page = 0,
  size = 20,
): Promise<PagedResult<ProductMovement>> {
  const q: Record<string, unknown> = { page, size };
  if (category) q.category = category;
  const response = await get<ApiResponse<PagedResult<ProductMovement>>>(`${BASE}/${id}/movements`, q);
  return response.data;
}

export const productService = {
  getProducts,
  getProduct,
  updateSellingPrice,
  getPriceHistory,
  getProductMovements,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductVariants,
  updateProductVariant,
  regenerateVariants,
  getActiveProducts,
  generateInternalCode,
  generateBarcode,
  getProductBatches,
  resolveProductByBarcode,
};
